import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { useSidebar } from '../sidebar/Sidebar';

import { WorkspaceSwitcherMenu, ProjectOption } from './WorkspaceSwitcherMenu';
import styles from './SidebarHeader.module.css';

export type { ProjectOption };

/* ── Icons ── */

const FolderIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M3 7C3 5.89543 3.89543 5 5 5H10.5858C10.851 5 11.1054 5.10536 11.2929 5.29289L12.7071 6.70711C12.8946 6.89464 13.149 7 13.4142 7H19C20.1046 7 21 7.89543 21 9V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17V7Z"
      fill="currentColor"
      opacity="0.25"
    />
    <path
      d="M3 9C3 7.89543 3.89543 7 5 7H19C20.1046 7 21 7.89543 21 9V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17V9Z"
      fill="currentColor"
    />
  </svg>
);

const ChevronUpDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M8.25 15L12 18.75L15.75 15M8.25 9L12 5.25L15.75 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronsLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M11 17L6 12L11 7M18 17L13 12L18 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronsRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M13 17L18 12L13 7M6 17L11 12L6 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Types ── */

export interface WorkspaceHeaderProps {
  title: string;
  subtitle?: string;
  projects?: ProjectOption[];
  onNewProject?: () => void;
  onImport?: () => void;
  onSettings?: () => void;
  className?: string;
}

interface MenuPosition {
  top: number;
  left: number;
  width: number;
}

/* ── Component ── */

const SidebarHeader = ({ title, subtitle, projects, onNewProject, onImport, onSettings, className }: WorkspaceHeaderProps) => {
  const { collapsed, toggle } = useSidebar();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const hasMenu = Boolean(projects?.length || onNewProject || onImport || onSettings);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        const menu = document.getElementById('workspace-switcher-menu');
        if (menu && menu.contains(e.target as Node)) return;
        setOpen(false);
      }
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  return (
    <>
      <div className={[styles.root, className].filter(Boolean).join(' ')}>
        {!collapsed && (
          <button
            ref={buttonRef}
            type="button"
            data-tour="sidebar-header"
            className={[styles.switcher, open ? styles.switcherOpen : ''].filter(Boolean).join(' ')}
            onClick={() => hasMenu && setOpen(prev => !prev)}
            aria-haspopup={hasMenu ? 'dialog' : undefined}
            aria-expanded={hasMenu ? open : undefined}
            aria-label={`${title} — workspace menu`}
          >
            <span className={styles.iconWrap}>
              <FolderIcon />
            </span>

            <span className={styles.text}>
              <span className={styles.title}>{title}</span>
              {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
            </span>

            {hasMenu && (
              <span className={styles.chevron}>
                <ChevronUpDownIcon />
              </span>
            )}
          </button>
        )}

        <button
          type="button"
          className={[styles.collapseBtn, collapsed ? styles.collapseBtnCollapsed : ''].filter(Boolean).join(' ')}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronsRightIcon /> : <ChevronsLeftIcon />}
        </button>
      </div>

      {open && hasMenu && menuPos && (
        <WorkspaceSwitcherMenu
          id="workspace-switcher-menu"
          style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
          projects={projects ?? []}
          onNewProject={onNewProject ? () => { onNewProject(); setOpen(false); } : undefined}
          onImportProject={onImport ? () => { onImport(); setOpen(false); } : undefined}
          onSettings={onSettings ? () => { onSettings(); setOpen(false); } : undefined}
          onProjectClick={() => setOpen(false)}
        />
      )}
    </>
  );
};

SidebarHeader.displayName = 'SidebarHeader';

export { SidebarHeader };
